import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-provider/context';
import { Center, Spinner } from '@chakra-ui/react';
import shallow from 'zustand/shallow';

/**
 * Protected route component
 * Restricts access based on authentication and role
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|string[]} props.requiredRole - Required role(s) to access this route
 */
const ProtectedRoute = ({ children, requiredRole }) => {
  const [user, isAuth, getAuth] = useAuth((state) => [state.user, state.isAuth, state.getAuth], shallow);

  // Check auth on mount
  React.useEffect(() => {
    if (!isAuth) {
      getAuth();
    }
  }, [isAuth, getAuth]);

  // Consider loading if we don't have auth state yet
  const isLoading = !isAuth && !user?.email;

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!isAuth || !user?.email) {
    return <Navigate to="/login" replace />;
  }

  // Support multiple roles
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    if (!allowedRoles.includes(user.role)) {
      // Redirect based on user role
      if (user.role === 'kepala_yayasan') {
        return <Navigate to="/yayasan-dashboard" replace />;
      }
      return <Navigate to="/home" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
